import { Role, type Role as RoleT } from './roles';

/**
 * The set of actions referenced by both the engine (authorize middleware)
 * and the UI (route guards / conditional UI). Add new actions here so both
 * sides stay in sync.
 */
export const Permission = {
  ACCOUNT_READ: 'account:read',
  ACCOUNT_UPDATE: 'account:update',
  ACCOUNT_SETTINGS_UPDATE: 'account:settings:update',
  MEMBER_INVITE: 'member:invite',
  MEMBER_LIST: 'member:list',
  MEMBER_UPDATE: 'member:update',

  MONITOR_READ: 'monitor:read',
  MONITOR_WRITE: 'monitor:write',

  MAINTENANCE_READ: 'maintenance:read',
  MAINTENANCE_WRITE: 'maintenance:write',

  STATUS_PAGE_READ: 'status_page:read',
  STATUS_PAGE_WRITE: 'status_page:write',

  ANNOUNCEMENT_READ: 'announcement:read',
  ANNOUNCEMENT_WRITE: 'announcement:write',

  AUDIT_READ_OWN: 'audit:read:own',
  AUDIT_READ_ALL: 'audit:read:all',

  // Super-admin only — listed here so authorize() has a consistent vocabulary.
  USER_INVITE: 'user:invite',
  USER_LIST: 'user:list',
  USER_UPDATE: 'user:update',
  USER_DELETE: 'user:delete',
  ACCOUNT_CREATE: 'account:create',
  ACCOUNT_LIST_ALL: 'account:list:all',
} as const;

export type Permission = (typeof Permission)[keyof typeof Permission];

export interface CanContext {
  role?: RoleT | null;
  isSuperAdmin?: boolean;
}

/**
 * Permission table. SuperAdmin shortcut is checked first in `can()`.
 */
const ROLE_PERMISSIONS: Record<RoleT, ReadonlySet<Permission>> = {
  [Role.ACCOUNT_ADMIN]: new Set<Permission>([
    Permission.ACCOUNT_READ,
    Permission.ACCOUNT_UPDATE,
    Permission.ACCOUNT_SETTINGS_UPDATE,
    Permission.MEMBER_INVITE,
    Permission.MEMBER_LIST,
    Permission.MEMBER_UPDATE,
    Permission.MONITOR_READ,
    Permission.MONITOR_WRITE,
    Permission.MAINTENANCE_READ,
    Permission.MAINTENANCE_WRITE,
    Permission.STATUS_PAGE_READ,
    Permission.STATUS_PAGE_WRITE,
    Permission.ANNOUNCEMENT_READ,
    Permission.ANNOUNCEMENT_WRITE,
    Permission.AUDIT_READ_OWN,
    Permission.AUDIT_READ_ALL,
  ]),
  [Role.ACCOUNT_EDITOR]: new Set<Permission>([
    Permission.ACCOUNT_READ,
    Permission.MONITOR_READ,
    Permission.MONITOR_WRITE,
    Permission.MAINTENANCE_READ,
    Permission.MAINTENANCE_WRITE,
    Permission.STATUS_PAGE_READ,
    Permission.STATUS_PAGE_WRITE,
    Permission.ANNOUNCEMENT_READ,
    Permission.ANNOUNCEMENT_WRITE,
    Permission.AUDIT_READ_OWN,
  ]),
  [Role.ACCOUNT_VIEWER]: new Set<Permission>([
    Permission.ACCOUNT_READ,
    Permission.MONITOR_READ,
    Permission.MAINTENANCE_READ,
    Permission.STATUS_PAGE_READ,
    Permission.ANNOUNCEMENT_READ,
  ]),
};

const SUPER_ADMIN_ONLY: ReadonlySet<Permission> = new Set([
  Permission.USER_INVITE,
  Permission.USER_LIST,
  Permission.USER_UPDATE,
  Permission.USER_DELETE,
  Permission.ACCOUNT_CREATE,
  Permission.ACCOUNT_LIST_ALL,
]);

/**
 * Returns true if the given role/super-admin combo grants `permission`.
 */
export function can(ctx: CanContext, permission: Permission): boolean {
  if (ctx.isSuperAdmin) return true;
  if (SUPER_ADMIN_ONLY.has(permission)) return false;
  if (!ctx.role) return false;
  return ROLE_PERMISSIONS[ctx.role].has(permission);
}
